import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Stack,
  Divider,
  Popover,
  Tooltip,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify/Iconify';

StatusCard.propTypes = {
  status: PropTypes.object,
  handleDetailsClick: PropTypes.func,
  deleteStatus: PropTypes.func,
  canUpdate: PropTypes.bool,
  canDelete: PropTypes.bool,
  showOptions: PropTypes.bool,
};

export default function StatusCard({
  status,
  handleDetailsClick,
  deleteStatus,
  canUpdate,
  canDelete,
  showOptions,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const isGlobal =
    status.statusId <= 5 ||
    status.departmentId === 0 ||
    (status.allDepartmentIds && status.allDepartmentIds.includes(0));
  const statusColor = status.statusId <= 5 ? status.statusColor : status.color;
  const statusDescription =
    status.statusId <= 5
      ? t(`statusDescription.${status.systemMappedStatus}`)
      : status.description;

  const [open, setOpen] = useState(null);
  const handleOpenMenu = (e) => {
    setOpen(e.currentTarget);
  };
  const handleCloseMenu = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card
        sx={{
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }} // Forces card to fill 100% of grid height
        onClick={() => handleDetailsClick(status)}
      >
        <Box bgcolor={statusColor} width="100%" height="30px" />

        <Stack spacing={0.5} py={2} position="relative">
          {/* 1. STATUS NAME  */}
          <Stack px={2} direction="row" justifyContent="center" alignItems="center">
            <Tooltip title={status.name}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: isSmallScreen ? '0.75rem' : '1rem',
                  textAlign: 'center',
                }}
              >
                {status.name}
              </Typography>
            </Tooltip>

            {status.statusId > 5 && showOptions && (
              <IconButton
                size="small"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenMenu(e);
                }}
                sx={{ position: 'absolute', right: 8 }}
              >
                <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
              </IconButton>
            )}
          </Stack>

          <Stack spacing={0.5} px={2} alignItems="center">
            {/* 2. EMAILS */}
            {!isGlobal && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Iconify
                  icon="eva:email-outline"
                  width={16}
                  sx={{ color: 'text.secondary' }}
                />
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? '0.6rem' : '0.75rem',
                    color: 'text.secondary', // Converts black text to dynamic grey for readability in dark mode
                  }}
                >
                  {status.correspondences?.length || 0}{' '}
                  {t('pages.statusPage.linkedEmails')}
                </Typography>
              </Stack>
            )}

            {/* 3. DEPARTMENT LABEL */}
            <Box sx={{ minHeight: '1.2rem', mt: 0.5 }}>
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '0.65rem' : '0.8rem',
                  color: 'text.secondary',
                  fontWeight: isGlobal ? 800 : 500,
                  display: 'block',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  lineHeight: 1.2,
                }}
              >
                {isGlobal
                  ? t('pages.statusPage.globalStatus')
                  : status.displayDepartments || status.department}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider />

        <Stack p={2} sx={{ flexGrow: 1 }}>
          {' '}
          {/* flexGrow: 1 pushes this section to align perfectly at the bottom */}
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              color: 'text.disabled',
              textTransform: 'uppercase',
            }}
          >
            {t('pages.statusPage.description')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: isSmallScreen ? '0.65rem' : '0.85rem', mt: 0.5 }}
          >
            {statusDescription}
          </Typography>
        </Stack>
      </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 170 } }}
      >
        {canUpdate && (
          <MenuItem
            onClick={() => {
              handleDetailsClick(status);
              handleCloseMenu();
            }}
          >
            <Iconify icon="eva:edit-fill" sx={{ mr: 2, width: 20, height: 20 }} />
            {t('popover.edit')}
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              deleteStatus(status);
              handleCloseMenu();
            }}
          >
            <Iconify icon="eva:trash-2-outline" sx={{ mr: 2, width: 20, height: 20 }} />
            {t('popover.delete')}
          </MenuItem>
        )}
      </Popover>
    </Box>
  );
}
